import { useEffect, useLayoutEffect, useRef } from 'react';
import { Input } from '@/components/cms/ui/input';
import { Card } from '@/components/ui/card';
import { Field } from '@/modules/primitives3D/editorShell';
import { FieldLabel } from '@/components/ui/text';
import { CoverImageTile } from './CoverImageTile';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import { usePublishFlowImages, type CoverEntry } from './usePublishFlowImages';
import {
  ERROR_INPUT_CLASS,
  isValidStoryName,
  storyNameError,
} from './validation';
import { FieldError, RequiredMark } from './FieldMarks';
import type { AnchorCategory, PublishDevice } from './types';
import { shownUrl } from '@/utils/localCopies';

const SCENE_INTRO_MAX = 500;

const BLANK_ENTRY: CoverEntry = { id: null, url: null, originalUrl: null, crop: null };

export interface SceneCardValues {
  name: string;
  intro: string;
}

export interface SceneCardStatus {
  landscapeReady: boolean;
  anchorReady: boolean;
  anchorCategory: AnchorCategory | null;
}

interface Props {
  sceneOriginId: string;
  sceneInstanceId: string;
  projectInstanceId?: string | null;
  devices: PublishDevice[];
  values: SceneCardValues;
  showErrors: boolean;
  onChange: (patch: Partial<SceneCardValues>) => void;
  onStatusChange: (status: SceneCardStatus) => void;
}

function usesAnchorInfo(a: AnchorCategory | null): boolean {
  return a === 'immersal' || a === 'image_anchor' || a === 'geospatial_anchor';
}

export function SceneCard({
  sceneOriginId,
  sceneInstanceId,
  projectInstanceId,
  devices,
  values,
  showErrors,
  onChange,
  onStatusChange,
}: Props) {
  const images = usePublishFlowImages({ projectInstanceId, sceneOriginId, sceneInstanceId, enabled: true });

  const anchorCategory = images.anchorCategory;
  const onAnchorableDevice = devices.includes('app') || devices.includes('appless');
  const needsAnchor = usesAnchorInfo(anchorCategory) && onAnchorableDevice;
  const isImageAnchor = anchorCategory === 'image_anchor';

  const isAutoLocationImage = anchorCategory === 'immersal' && images.scanImageIsAutoThumbnail;
  const locationImageUrl = isAutoLocationImage ? null : images.scanImageUrl;
  const anchorImageUrl = isImageAnchor ? images.imageAnchorUrl : locationImageUrl;

  const landscapeReady = Boolean(images.sceneLandscape.url);
  const anchorReady = !needsAnchor || Boolean(anchorImageUrl);

  // onStatusChange is recreated each render; keep it out of the deps below to avoid a loop.
  const statusRef = useRef(onStatusChange);
  useLayoutEffect(() => {
    statusRef.current = onStatusChange;
  });
  useEffect(() => {
    statusRef.current({ landscapeReady, anchorReady, anchorCategory });
  }, [landscapeReady, anchorReady, anchorCategory]);

  const nameInvalid = showErrors && !isValidStoryName(values.name);
  const landscapeError = showErrors && !landscapeReady;
  const anchorError = showErrors && !anchorReady;

  return (
    <Card>
      <div className="grid grid-cols-[10rem_1fr] gap-4">
        <div className="space-y-3">
          <CoverImageTile
            aspect="16/9"
            fullWidth
            label="Landscape (16:9)"
            category="SceneLandscape"
            entry={images.sceneLandscape}
            displayUrl={images.sceneLandscape.url}
            error={landscapeError}
            projectInstanceId={projectInstanceId}
            sceneOriginId={sceneOriginId}
            onSave={(payload) => images.saveImage('SceneLandscape', payload)}
            onRemove={() => images.removeImage('SceneLandscape')}
          />
          {landscapeError && <FieldError>Add a landscape cover image</FieldError>}

          {needsAnchor && !isImageAnchor && (
            <>
              <CoverImageTile
                aspect="1/1"
                fullWidth
                label="Location image (1:1)"
                category="ScanImage"
                entry={isAutoLocationImage ? BLANK_ENTRY : images.scanImage}
                displayUrl={locationImageUrl}
                error={anchorError}
                projectInstanceId={projectInstanceId}
                sceneOriginId={sceneOriginId}
                onSave={images.saveScanImage}
                onRemove={images.removeScanImage}
              />
              {anchorError ? (
                <FieldError>Add a location image</FieldError>
              ) : (
                <p className="font-lureo-font-prose text-lureo-12 text-lureo-ink-subtle leading-[1.4]">
                  Where your audience points their phone.
                </p>
              )}
            </>
          )}

          {needsAnchor && isImageAnchor && (
            <>
              <div
                className={`w-full rounded-lureo-radius border-lureo-border-width bg-lureo-neutral-subtle overflow-hidden flex items-center justify-center aspect-square ${
                  anchorError ? 'border-lureo-danger-ink' : 'border-lureo-line'
                }`}
              >
                {anchorImageUrl ? (
                  <img src={shownUrl(anchorImageUrl)} alt="" className="w-full h-full object-contain" />
                ) : (
                  <span className="font-lureo-font-ui text-lureo-12 text-lureo-ink-subtle px-2 text-center">No image anchor set</span>
                )}
              </div>
              {anchorError && <FieldError>Set the image anchor in the story editor</FieldError>}
            </>
          )}
        </div>

        <div className="min-w-0 space-y-3">
          <Field>
            <FieldLabel mb>
              Story name
              <RequiredMark />
            </FieldLabel>
            <Input
              className={nameInvalid ? ERROR_INPUT_CLASS : undefined}
              value={values.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="Untitled story"
            />
            {nameInvalid && <FieldError>{storyNameError(values.name)}</FieldError>}
          </Field>

          <Field>
            <FieldLabel mb>Introduction</FieldLabel>
            <AutoGrowTextarea
              value={values.intro}
              onChange={(e) => onChange({ intro: e.target.value })}
              placeholder="What happens in this story?"
              maxLength={SCENE_INTRO_MAX}
              minHeightPx={72}
            />
          </Field>
        </div>
      </div>
    </Card>
  );
}
